import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { ShieldCheck, Clock, Sparkles, BadgeCheck } from 'lucide-react'
import { HeroSection } from '@/components/sections/HeroSection'
import { TextSection } from '@/components/sections/TextSection'
import { ServiceCard } from '@/components/ui/ServiceCard'
import { QuoteCTA } from '@/components/sections/QuoteCTA'
import { Section } from '@/components/ui/Section'
import { Container } from '@/components/ui/Container'

const serviceImages: Record<string, string> = {
  pharma: '/images/service-pharma.jpg',
  food: '/images/service-food.jpg',
  security: '/images/fleet-05.jpg',
  fashion: '/images/service-fashion.jpg',
  logistics: '/images/service-logistics.jpg',
  tempControl: '/images/service-temp-control.jpg',
  ftl: '/images/service-ftl.jpg',
  groupage: '/images/service-groupage.jpg',
  intermodal: '/images/service-intermodal.jpg',
}

const serviceKeys = [
  'pharma',
  'food',
  'security',
  'fashion',
  'logistics',
  'tempControl',
  'ftl',
  'groupage',
  'intermodal',
]

export default function Services() {
  const { t, i18n } = useTranslation('common')

  return (
    <>
      <Helmet>
        <title>{t('services.seo.title')}</title>
        <meta name="description" content={t('services.seo.description')} />
        <html lang={i18n.language} />
      </Helmet>

      <HeroSection
        imageSrc="/images/services-hero.jpg"
        title={t('services.hero.title')}
        subtitle={t('services.hero.subtitle')}
        slogan={t('services.hero.slogan')}
        primaryCta={{ label: t('buttons.contactUs'), to: 'contacts' }}
        secondaryCta={{ label: t('nav.services'), to: 'services' }}
      />

      <TextSection
        heading={t('services.intro.heading')}
        body={t('services.intro.body')}
        align="center"
      />

      <section className="py-20 md:py-28 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {serviceKeys.map((key) => (
              <ServiceCard
                key={key}
                serviceId={key}
                imageSrc={serviceImages[key]}
                title={t(`services.cards.${key}.title`)}
                description={t(`services.cards.${key}.description`)}
              />
            ))}
          </div>
        </div>
      </section>

      <QuoteCTA />

      <Section variant="light">
        <Container>
          <h2 className="text-2xl md:text-4xl font-bold text-brand-dark mb-12 text-center">
            {t('services.commitments.heading')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { key: 'reliability', Icon: BadgeCheck },
              { key: 'safety', Icon: ShieldCheck },
              { key: 'timeliness', Icon: Clock },
              { key: 'customization', Icon: Sparkles },
            ].map(({ key, Icon }, index) => (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-white p-6 md:p-8 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300 text-center"
              >
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-brand-red/10">
                  <Icon className="h-7 w-7 text-brand-red" />
                </div>
                <h3 className="text-lg font-bold text-brand-dark mb-3">
                  {t(`services.commitments.${key}.title`)}
                </h3>
                <p className="text-brand-text leading-relaxed">
                  {t(`services.commitments.${key}.description`)}
                </p>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>
    </>
  )
}
